'use client';

import type { ButtonVariantProps } from '../types';
import ButtonComponent, { ButtonSizes, ButtonVariants } from '@pixum/button';
import Download from '@pixum/svg-icon/lib/Standard/Download';
import Text, { TextTag, TEXT_VARIANT } from '@pixum/text';
import { useDeviceInfo, useSoftwareDownload } from 'hooks';

/**
 * Props for ButtonWithDirectSoftwareDownload component.
 * Extends ButtonVariantProps but makes device optional since it's not used.
 */
export interface DirectDownloadButtonProps extends Omit<ButtonVariantProps, 'device'> {
  device?: ButtonVariantProps['device'];
}

/**
 * `Direct Software Download Button` component triggers direct software download via PSI service.
 *
 * This button does NOT require a URL in Contentful. It determines the download URL
 * dynamically based on the user's operating system by calling the PSI service.
 *
 * @component
 * @param link - A Link Object (URL not required).
 * @param device - Device type (optional, not used by this component as it uses client-side detection).
 */
export function ButtonWithDirectSoftwareDownload({ link }: DirectDownloadButtonProps) {
  const deviceInfo = useDeviceInfo();
  const triggerDownload = useSoftwareDownload(deviceInfo.operatingSystem);

  return (
    <ButtonComponent
      className="button--download button-for-desktop"
      label={link?.title}
      size={ButtonSizes.LARGE}
      type="button"
      variant={ButtonVariants.SECONDARY}
      onClick={triggerDownload}
    >
      <Download />
      <div className="button--download--text">
        <Text tag={TextTag.SPAN} variant={TEXT_VARIANT.BUTTON}>
          {link?.title || ''}
        </Text>
      </div>
    </ButtonComponent>
  );
}
